import { Menu } from "lucide-react";
import { useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { Breadcrumb } from "@/components/navigation/Breadcrumb";
import { UserAvatarMenu } from "@/components/navigation/UserAvatarMenu";

import "./HubTopBar.css";

export interface HubTopBarProps {
  readonly onOpenMobileDrawer: () => void;
  /**
   * Mobile drawer open state — powers aria-expanded on the hamburger so
   * assistive tech announces whether the drawer is currently open.
   */
  readonly mobileDrawerOpen?: boolean;
  /**
   * DOM id of the mobile drawer element the hamburger controls. Wired into
   * aria-controls so the relationship is discoverable.
   */
  readonly mobileDrawerId?: string;
  /**
   * Optional slot for a notification bell. Defaults to undefined so no bell
   * renders — the dashboard has no notifications endpoint yet.
   */
  readonly notificationBell?: ReactNode;
}

/** First-level page titles shown instead of a breadcrumb. */
const ROOT_PAGE_TITLE: Record<string, string> = {
  "": "Home",
  "/": "Home",
};

/**
 * Top bar inside HubSidebarLayout — shows the page title at root,
 * a breadcrumb deeper, and the user avatar menu on the right.
 */
export function HubTopBar({
  onOpenMobileDrawer,
  mobileDrawerOpen,
  mobileDrawerId,
  notificationBell,
}: HubTopBarProps): JSX.Element {
  const { pathname } = useLocation();
  const normalizedPath = pathname.replace(/\/$/, "");
  const pageTitle = ROOT_PAGE_TITLE[normalizedPath];
  const isFirstLevel = pageTitle !== undefined;

  return (
    <header className="hub-topbar" role="banner">
      {/* Left: mobile hamburger + page title or breadcrumb */}
      <div className="hub-topbar__left">
        <button
          id="hub-mobile-menu-trigger"
          type="button"
          className="hub-topbar__hamburger"
          onClick={onOpenMobileDrawer}
          aria-label="Open navigation menu"
          aria-haspopup="dialog"
          aria-expanded={mobileDrawerOpen ?? undefined}
          aria-controls={mobileDrawerId}
        >
          <Menu size={22} />
        </button>

        {isFirstLevel ? (
          <span className="hub-topbar__page-title">{pageTitle}</span>
        ) : (
          <div className="hub-topbar__breadcrumb">
            <Breadcrumb />
          </div>
        )}
      </div>

      {/* Right: optional notification bell + avatar */}
      <div className="hub-topbar__right">
        {notificationBell}
        <UserAvatarMenu />
      </div>
    </header>
  );
}
